import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Sparkles, CheckCircle2, Trash2, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { api, API, errMsg, has } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/app/PageHeader";
import { Md } from "./Analyst";
import { PpcCebuano } from "../components/app/PpcCebuano";
import { PpcSend } from "../components/app/PpcSend";
import { PpcMembers } from "../components/app/PpcMembers";

const iso = (d) => d.toLocaleDateString("en-CA");

export async function streamSse(path, body, onEvent) {
  const res = await fetch(`${API}${path}`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Could not start");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    parts.forEach((p) => { if (p.startsWith("data: ")) onEvent(JSON.parse(p.slice(6))); });
  }
}

const downloadPdf = async (d) => {
  try {
    const r = await api.get(`/analyst/ppc-drafts/${d.draft_id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = `ppc-summary-${d.end}.pdf`; a.click(); URL.revokeObjectURL(url);
  } catch { toast.error("Could not create the PDF"); }
};

const Editor = ({ d, onChanged, onDeleted, membersVer }) => {
  const { user } = useAuth();
  const [content, setContent] = useState(d.content);
  const [title, setTitle] = useState(d.title);
  const draft = d.status === "draft";
  const save = async () => { try { await api.patch(`/analyst/ppc-drafts/${d.draft_id}`, { title, content }); toast.success("Saved"); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  const approve = async () => { try { await api.patch(`/analyst/ppc-drafts/${d.draft_id}`, { title, content }); await api.post(`/analyst/ppc-drafts/${d.draft_id}/approve`); toast.success("Summary approved"); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async () => { try { await api.delete(`/analyst/ppc-drafts/${d.draft_id}`); toast.success("Deleted"); onDeleted(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4" data-testid="draft-editor">
      <div className="flex flex-wrap items-center gap-2">
        {draft ? <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900" data-testid="draft-status">Draft · awaiting the Parish Priest's approval</span>
          : <span className="flex items-center gap-1 rounded-full bg-[#285E3B] px-3 py-1 text-xs font-semibold text-white" data-testid="draft-status"><CheckCircle2 className="h-3 w-3" />Approved by {d.approved_by}</span>}
        {d.edited && <span className="text-xs text-stone-500" data-testid="draft-edited">Edited by people since the AI draft{d.last_edited_by ? ` (last: ${d.last_edited_by})` : ""}</span>}
      </div>
      <Input value={title} disabled={!draft} onChange={(e) => setTitle(e.target.value)} data-testid="draft-title-input" />
      <Tabs defaultValue={draft ? "edit" : "preview"}>
        <TabsList className="bg-[#F5F0E6]"><TabsTrigger value="edit" data-testid="draft-tab-edit">Edit</TabsTrigger><TabsTrigger value="preview" data-testid="draft-tab-preview">Preview</TabsTrigger></TabsList>
        <TabsContent value="edit"><Textarea rows={24} value={content} disabled={!draft} onChange={(e) => setContent(e.target.value)} className="font-mono-data text-xs" data-testid="draft-content-input" /></TabsContent>
        <TabsContent value="preview"><div className="rounded-xl border border-amber-700/15 bg-white p-6"><Md text={content} /></div></TabsContent>
      </Tabs>
      <div className="flex flex-wrap gap-2">
        {draft && <Button variant="outline" onClick={save} data-testid="draft-save-button">Save changes</Button>}
        {draft && has(user, "priority:decide") && (
          <AlertDialog>
            <AlertDialogTrigger asChild><Button className="bg-[#285E3B] hover:bg-[#1E462C]" data-testid="draft-approve-button">Approve for the PPC</Button></AlertDialogTrigger>
            <AlertDialogContent className="bg-white">
              <AlertDialogHeader><AlertDialogTitle>Approve this summary?</AlertDialogTitle><AlertDialogDescription>Please confirm you have checked the figures and suggestions. After approval the text is locked.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={approve} data-testid="draft-approve-confirm">Approve</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        <Button variant="outline" onClick={() => downloadPdf(d)} data-testid="draft-pdf-button"><FileText className="h-4 w-4 mr-1" />PDF</Button>
        {(draft || has(user, "priority:decide")) && (
          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="ghost" data-testid="draft-delete-button"><Trash2 className="h-4 w-4 mr-1" />Delete</Button></AlertDialogTrigger>
            <AlertDialogContent className="bg-white">
              <AlertDialogHeader><AlertDialogTitle>Delete this {draft ? "draft" : "approved summary"}?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={del} data-testid="draft-delete-confirm">Delete</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
      {!draft && has(user, "priority:decide") && <PpcSend d={d} onChanged={onChanged} membersVer={membersVer} />}
      {!draft && <PpcCebuano d={d} onChanged={onChanged} />}
    </div>
  );
};

export default function PpcDrafts() {
  const { user } = useAuth();
  const [membersVer, setMembersVer] = useState(0);
  const [list, setList] = useState([]);
  const [cur, setCur] = useState(null);
  const [live, setLive] = useState("");
  const [busy, setBusy] = useState(false);
  const [r, setR] = useState({ start: iso(new Date(Date.now() - 30 * 864e5)), end: iso(new Date()) });
  const load = useCallback(() => api.get("/analyst/ppc-drafts").then((x) => setList(x.data)), []);
  const open = (id) => api.get(`/analyst/ppc-drafts/${id}`).then((x) => setCur(x.data));
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true); setCur(null); setLive("");
    let id = null;
    try {
      await streamSse("/analyst/ppc-drafts", r, (ev) => {
        if (ev.type === "meta") id = ev.draft_id;
        if (ev.type === "delta") setLive((t) => t + ev.text);
        if (ev.type === "error") toast.error(ev.message);
        if (ev.type === "done") id = ev.draft_id;
      });
      load();
      if (id) await open(id).catch(() => {});
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); setLive(""); }
  };

  return (
    <div className="space-y-6">
      <Link to="/analyst" className="inline-flex items-center gap-1 text-sm text-amber-900" data-testid="back-to-analyst"><ArrowLeft className="h-4 w-4" />Pastoral Analyst</Link>
      <PageHeader eyebrow="AI-assisted · human-approved" title="Monthly PPC Summary Drafts" testId="ppc-drafts-header">
        The Analyst prepares a first draft from verified parish data. Administrators can edit it; only the Parish Priest approves it for the Parish Pastoral Council.
      </PageHeader>
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-amber-700/15 bg-white p-4" data-testid="draft-generate-bar">
        <label className="space-y-1"><span className="text-xs text-stone-600">From</span><Input type="date" value={r.start} onChange={(e) => setR({ ...r, start: e.target.value })} data-testid="draft-start-input" /></label>
        <label className="space-y-1"><span className="text-xs text-stone-600">To</span><Input type="date" value={r.end} onChange={(e) => setR({ ...r, end: e.target.value })} data-testid="draft-end-input" /></label>
        <Button disabled={busy} onClick={generate} className="bg-amber-900 hover:bg-[#5B210B]" data-testid="draft-generate-button"><Sparkles className="h-4 w-4 mr-2" />{busy ? "Drafting… (about a minute)" : "Draft monthly PPC summary"}</Button>
      </div>
      {has(user, "ppc:members") && <PpcMembers onChange={() => setMembersVer((v) => v + 1)} />}
      <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
        <aside className="space-y-2" data-testid="drafts-list">
          {list.map((d) => (
            <button key={d.draft_id} onClick={() => open(d.draft_id)} className={`block w-full rounded-lg px-3 py-2 text-left text-sm ${cur?.draft_id === d.draft_id ? "bg-amber-100" : "hover:bg-[#F5F0E6]"}`} data-testid={`draft-item-${d.draft_id}`}>
              <p className="truncate font-semibold text-stone-800">{d.title}</p>
              <p className="text-xs text-stone-500">{d.status === "approved" ? `Approved · ${d.approved_by}` : "Draft"}{d.ceb_status === "approved" ? " · Cebuano ✓" : ""} · {d.created_by}</p>
            </button>
          ))}
          {!list.length && <p className="text-sm text-stone-500">No drafts yet.</p>}
        </aside>
        <section className="min-w-0">
          {busy && <div className="rounded-xl border border-amber-700/15 bg-white p-6" data-testid="draft-live">{live ? <Md text={live} /> : <p className="text-sm italic text-stone-500">Reading the parish data…</p>}</div>}
          {!busy && cur && <Editor key={`${cur.draft_id}-${cur.updated_at}-${cur.status}-${cur.ceb_status}-${cur.ceb_updated_at}-${(cur.sends || []).length}`} d={cur} membersVer={membersVer} onChanged={() => { open(cur.draft_id); load(); }} onDeleted={() => { setCur(null); load(); }} />}
        </section>
      </div>
    </div>
  );
}
